// React Query hooks for the Leagues data (FE3). One place that maps a scope to
// the backend read, so widgets just call the hook and render its state. Queries
// are disabled until their inputs are known (a widget may mount before a league/
// season is picked); reference lists (competitions/seasons) are cached a while.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type {
  CompetitionsResponse,
  SeasonsResponse,
  LeagueTableResponse,
  MatchesResponse,
} from "@catbento/shared";
import { apiGet } from "../api.js";
import { compParam, type CompetitionArg } from "./players.js";

const FIVE_MIN = 5 * 60 * 1000;

/** All leagues for the picker (both genders). Cached — it rarely changes. */
export function useCompetitions(): UseQueryResult<CompetitionsResponse, Error> {
  return useQuery({
    queryKey: ["competitions"],
    queryFn: () => apiGet<CompetitionsResponse>("/competitions"),
    staleTime: FIVE_MIN,
  });
}

/** Seasons for one league (most-recent first). Disabled until a league is set. */
export function useSeasons(competitionId: number | null): UseQueryResult<SeasonsResponse, Error> {
  return useQuery({
    queryKey: ["seasons", competitionId],
    enabled: competitionId !== null,
    queryFn: () => apiGet<SeasonsResponse>(`/seasons?competitionId=${competitionId}`),
    staleTime: FIVE_MIN,
  });
}

/** Standings for a league+season. Disabled until both are set. */
export function useLeagueTable(
  competitionId: number | null,
  seasonId: number | null
): UseQueryResult<LeagueTableResponse, Error> {
  return useQuery({
    queryKey: ["league-table", competitionId, seasonId],
    enabled: competitionId !== null && seasonId !== null,
    queryFn: () =>
      apiGet<LeagueTableResponse>(`/league-table?competitionId=${competitionId}&seasonId=${seasonId}`),
  });
}

/** Fixtures/results for a league+season. Disabled until both are set. */
export function useMatches(
  competition: CompetitionArg,
  seasonId: number | null
): UseQueryResult<MatchesResponse, Error> {
  const cid = compParam(competition);
  return useQuery({
    queryKey: ["matches", cid, seasonId],
    enabled: cid !== null && seasonId !== null,
    queryFn: () => apiGet<MatchesResponse>(`/matches?competitionId=${cid}&seasonId=${seasonId}`),
  });
}
